/**
 * MatrixGrid Component
 * Virtualized grid for displaying training matrix data
 *
 * Features:
 * - Row and column virtualization for large datasets
 * - Sticky header row and first column (employee names)
 * - Supports highlighting rows/columns
 * - Drag-and-drop cell interactions
 * - Overall compliance stats in top-left corner
 */

import { memo, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useVirtualizer } from '@tanstack/react-virtual';
import {
  Plus,
  UserPlus,
  Eye,
  FileText,
  MoreVertical
} from 'lucide-react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger
} from '@/components/ui/tooltip';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  horizontalListSortingStrategy
} from '@dnd-kit/sortable';

import ColumnHeader from './ColumnHeader';
import MatrixCell from './MatrixCell';

// Constants matching existing implementation
const CELL_WIDTH = 112;
const CELL_HEIGHT = 112;
const HEADER_HEIGHT = 112;
const TOP_OFFSET = 46; // For sticky positioning below tabs

/**
 * CornerCell - Top-left cell with overall compliance stats
 */
const CornerCell = memo(({
  sidebarStats,
  nameColumnExpanded
}) => {
  return (
    <th
      className="sticky top-[46px] left-0 z-40 bg-white border-b border-r border-gray-200 p-0 h-[112px] text-left"
      style={{
        width: nameColumnExpanded ? '250px' : '60px',
        transition: 'width 0.5s ease-in-out'
      }}
    >
      <div className="h-full flex flex-col p-2 pl-4">
        {/* Overall Compliance Stats */}
        <TooltipProvider delayDuration={300} skipDelayDuration={0}>
          <Tooltip delayDuration={300} disableHoverableContent={false}>
            <TooltipTrigger asChild>
              <div className="flex-1 flex flex-col pr-2 cursor-help items-start">
                {/* Top content area - grows to push progress bar down */}
                <div className="flex-1 flex flex-col justify-center gap-1 w-full">
                  {/* Compliance Label */}
                  <div>
                    <span className="text-xs font-medium text-gray-600">Overall Compliance</span>
                  </div>

                  {/* Stats Row */}
                  <div className="flex items-center gap-2 text-[10px]">
                    <div className="flex items-center gap-1 px-1.5 py-0.5 bg-green-50 rounded">
                      <div className="w-1.5 h-1.5 rounded-full bg-green-500"></div>
                      <span className="font-medium text-green-700">{sidebarStats.compliant + sidebarStats.compliantCrossSpace}</span>
                    </div>
                    <div className="flex items-center gap-1 px-1.5 py-0.5 bg-amber-50 rounded">
                      <div className="w-1.5 h-1.5 rounded-full bg-amber-500"></div>
                      <span className="font-medium text-amber-700">{sidebarStats.expiring + sidebarStats.expiringCrossSpace}</span>
                    </div>
                    <div className="flex items-center gap-1 px-1.5 py-0.5 bg-red-50 rounded">
                      <div className="w-1.5 h-1.5 rounded-full bg-red-500"></div>
                      <span className="font-medium text-red-700">{sidebarStats.expired}</span>
                    </div>
                    <div className="flex items-center gap-1 px-1.5 py-0.5 bg-red-50 rounded">
                      <div className="w-1.5 h-1.5 rounded-full bg-red-500"></div>
                      <span className="font-medium text-red-700">{sidebarStats.missing}</span>
                    </div>
                  </div>
                </div>

                {/* Progress Bar - pinned to bottom to align with column header bars */}
                <div className="w-full px-1 mt-1">
                  <div className="w-full h-1.5 bg-gray-200 rounded-full overflow-hidden flex">
                    {(() => {
                      const total = sidebarStats.totalRequired || 1;
                      const validPct = ((sidebarStats.compliant || 0) / total) * 100;
                      const validCrossPct = ((sidebarStats.compliantCrossSpace || 0) / total) * 100;
                      const expiringPct = ((sidebarStats.expiring || 0) / total) * 100;
                      const expiringCrossPct = ((sidebarStats.expiringCrossSpace || 0) / total) * 100;
                      const expiredPct = ((sidebarStats.expired || 0) / total) * 100;
                      const missingPct = ((sidebarStats.missing || 0) / total) * 100;
                      return (
                        <>
                          {validPct > 0 && <div className="h-1.5 bg-green-500" style={{ width: `${validPct}%` }} />}
                          {validCrossPct > 0 && <div className="h-1.5 bg-green-300" style={{ width: `${validCrossPct}%` }} />}
                          {expiringPct > 0 && <div className="h-1.5 bg-amber-500" style={{ width: `${expiringPct}%` }} />}
                          {expiringCrossPct > 0 && <div className="h-1.5 bg-amber-300" style={{ width: `${expiringCrossPct}%` }} />}
                          {expiredPct > 0 && <div className="h-1.5 bg-red-500" style={{ width: `${expiredPct}%` }} />}
                          {missingPct > 0 && <div className="h-1.5 bg-red-500" style={{ width: `${missingPct}%` }} />}
                        </>
                      );
                    })()}
                  </div>
                  <div className="text-[9px] text-gray-500 text-center mt-0.5 font-medium">
                    {sidebarStats.overallCompliancePercentage}%
                  </div>
                </div>
              </div>
            </TooltipTrigger>
            <TooltipContent side="right" className="bg-white border border-gray-200 shadow-lg p-3 max-w-xs">
              <div className="space-y-2">
                <div className="text-sm font-semibold text-gray-900 border-b pb-2">Overall Compliance Details</div>

                {/* Team & Requirements Overview */}
                <div className="grid grid-cols-2 gap-2 text-xs pb-2 border-b">
                  <div>
                    <div className="text-gray-500">Team Members</div>
                    <div className="font-semibold text-gray-900">{sidebarStats.totalEmployees || 0}</div>
                  </div>
                  <div>
                    <div className="text-gray-500">Qualifications</div>
                    <div className="font-semibold text-gray-900">{sidebarStats.totalRequirements || 0}</div>
                  </div>
                </div>

                {/* Status Breakdown */}
                <div className="space-y-1.5 text-xs">
                  <div className="bg-green-50 rounded-md px-2 py-1.5 border border-green-200">
                    <div className="flex flex-col gap-0.5">
                      <div className="flex items-center justify-between">
                        <span className="text-green-700 font-medium">Valid</span>
                        <span className="font-bold text-green-600">{(sidebarStats.compliant || 0) + (sidebarStats.compliantCrossSpace || 0)}</span>
                      </div>
                      <div className="flex items-center gap-2 text-[10px] text-green-600 opacity-70">
                        <span>Assigned: {sidebarStats.compliant || 0}</span>
                        <span>Unassigned: {sidebarStats.compliantCrossSpace || 0}</span>
                      </div>
                    </div>
                  </div>
                  <div className="bg-orange-50 rounded-md px-2 py-1.5 border border-orange-200">
                    <div className="flex flex-col gap-0.5">
                      <div className="flex items-center justify-between">
                        <span className="text-orange-700 font-medium">Expiring Soon</span>
                        <span className="font-bold text-orange-600">{(sidebarStats.expiring || 0) + (sidebarStats.expiringCrossSpace || 0)}</span>
                      </div>
                      <div className="flex items-center gap-2 text-[10px] text-orange-600 opacity-70">
                        <span>Assigned: {sidebarStats.expiring || 0}</span>
                        <span>Unassigned: {sidebarStats.expiringCrossSpace || 0}</span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-1.5">
                      <div className="w-2 h-2 rounded-full bg-red-500"></div>
                      <span className="text-gray-700">Expired</span>
                    </div>
                    <span className="font-semibold text-red-700">{sidebarStats.expired || 0}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-1.5">
                      <div className="w-2 h-2 rounded-full bg-red-500"></div>
                      <span className="text-gray-700">Missing</span>
                    </div>
                    <span className="font-semibold text-red-700">{sidebarStats.missing || 0}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-1.5">
                      <div className="w-2 h-2 rounded-full bg-primary"></div>
                      <span className="text-gray-700">Booked</span>
                    </div>
                    <span className="font-semibold text-primary">{sidebarStats.booked || 0}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-1.5">
                      <div className="w-2 h-2 rounded-full bg-primary"></div>
                      <span className="text-gray-700">Quotes</span>
                    </div>
                    <span className="font-semibold text-primary">{sidebarStats.quotes || 0}</span>
                  </div>
                </div>

                {/* Required vs Assigned */}
                <div className="pt-2 border-t grid grid-cols-2 gap-2 text-xs">
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600">Required:</span>
                    <span className="font-bold text-gray-900">{sidebarStats.totalRequired || 0}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600">Assigned:</span>
                    <span className="font-bold text-gray-900">{sidebarStats.totalAssigned || 0}</span>
                  </div>
                </div>
              </div>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>
    </th>
  );
});

CornerCell.displayName = 'CornerCell';

/**
 * RowHeader - Sticky employee name cell
 */
const RowHeader = memo(({
  member,
  isHighlighted,
  nameColumnExpanded,
  employeeStats,
  toggleRowHighlight,
  navigate
}) => {
  return (
    <td
      className={`sticky left-0 z-20 border-b border-r border-gray-200 p-0 group h-[112px] transition-all duration-300 ${isHighlighted ? 'bg-blue-50' : 'bg-white'}`}
      style={{
        width: nameColumnExpanded ? '250px' : '60px'
      }}
    >
      <div
        className="h-full flex items-center overflow-hidden relative cursor-pointer"
        onClick={(e) => {
          e.stopPropagation();
          toggleRowHighlight(member.id);
        }}
      >
        <div className={`flex-1 ${nameColumnExpanded ? 'px-2' : 'pl-2 pr-1'}`}>
          <AnimatePresence mode="wait">
            {nameColumnExpanded ? (
              <motion.div
                key="expanded"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.2 }}
                className="flex items-center gap-1 w-full rounded-xl px-2 py-1.5 hover:bg-gray-50 transition-all duration-200 group/item"
              >
                <TooltipProvider delayDuration={300} skipDelayDuration={0}>
                  <Tooltip delayDuration={300} disableHoverableContent={false}>
                    <TooltipTrigger asChild>
                      <div className="flex items-center gap-2 flex-1 min-w-0 overflow-hidden cursor-pointer">
                        <div className="relative flex-shrink-0">
                          {member.avatar_url ? (
                            <img
                              src={member.avatar_url}
                              alt={member.name}
                              className="w-9 h-9 rounded-xl object-cover shadow-sm group-hover/item:shadow-md transition-all duration-200 group-hover/item:scale-105"
                            />
                          ) : (
                            <div className="w-9 h-9 bg-gradient-to-br from-gray-100 to-gray-200 rounded-xl flex items-center justify-center text-gray-700 text-xs font-medium shadow-sm group-hover/item:shadow-md transition-all duration-200 group-hover/item:scale-105">
                              {member.name.split(' ').map(n => n[0]).join('').toUpperCase()}
                            </div>
                          )}
                        </div>
                        <div className="flex-1 min-w-0 overflow-hidden">
                          <p className="text-xs font-medium text-gray-900 truncate group-hover/item:text-gray-700 transition-colors">
                            {member.name}
                          </p>
                          {/* Progress bar - segmented */}
                          {employeeStats && (
                            <div className="mt-0.5">
                              {employeeStats.totalAssigned > 0 ? (
                                <>
                                  <div className="w-full bg-gray-200 rounded-full h-1 overflow-hidden flex">
                                    {/* Segmented progress bar */}
                                    {(() => {
                                      const total = employeeStats.totalAssigned || 1;
                                      const validPct = ((employeeStats.valid || 0) / total) * 100;
                                      const expiringPct = ((employeeStats.expiring || 0) / total) * 100;
                                      const expiredPct = ((employeeStats.expired || 0) / total) * 100;
                                      const missingPct = ((employeeStats.missing || 0) / total) * 100;
                                      return (
                                        <>
                                          {validPct > 0 && <div className="h-1 bg-green-500" style={{ width: `${validPct}%` }} />}
                                          {expiringPct > 0 && <div className="h-1 bg-amber-500" style={{ width: `${expiringPct}%` }} />}
                                          {expiredPct > 0 && <div className="h-1 bg-red-500" style={{ width: `${expiredPct}%` }} />}
                                          {missingPct > 0 && <div className="h-1 bg-red-500" style={{ width: `${missingPct}%` }} />}
                                        </>
                                      );
                                    })()}
                                  </div>
                                  <div className="text-[9px] text-gray-500 mt-0.5">
                                    {employeeStats.compliancePercentage}%
                                  </div>
                                </>
                              ) : (
                                <div className="text-[9px] text-gray-400 mt-0.5">
                                  N/A
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    </TooltipTrigger>
                    <TooltipContent side="right" className="max-w-xs bg-white border border-gray-200 shadow-lg p-3" sideOffset={10}>
                      {/* Header with member name */}
                      <div className="font-semibold text-sm text-gray-900 mb-3 pb-2 border-b border-gray-100">{member.name}</div>

                      {employeeStats && (
                        employeeStats.totalAssigned > 0 ? (
                          <>
                            {/* Compliance percentage - highlighted */}
                            <div className={`rounded-lg p-3 mb-3 ${
                              employeeStats.compliancePercentage >= 80
                                ? 'bg-green-50 border border-green-200'
                                : employeeStats.compliancePercentage >= 50
                                ? 'bg-yellow-50 border border-yellow-200'
                                : 'bg-red-50 border border-red-200'
                            }`}>
                              <div className="flex items-center justify-between gap-4">
                                <span className="text-xs font-medium text-gray-600">Overall Compliance</span>
                                <span className={`text-lg font-bold ${
                                  employeeStats.compliancePercentage >= 80 ? 'text-green-600'
                                  : employeeStats.compliancePercentage >= 50 ? 'text-yellow-600'
                                  : 'text-red-600'
                                }`}>
                                  {employeeStats.compliancePercentage}%
                                </span>
                              </div>
                            </div>

                            {/* Stats Grid */}
                            <div className="grid grid-cols-2 gap-2 text-xs">
                              <div className="bg-green-50 rounded-md px-2 py-1.5 border border-green-200">
                                <div className="flex items-center justify-between">
                                  <span className="text-green-700 font-medium">Valid</span>
                                  <span className="font-bold text-green-600">{employeeStats.valid}</span>
                                </div>
                              </div>
                              <div className="bg-yellow-50 rounded-md px-2 py-1.5 border border-yellow-200">
                                <div className="flex items-center justify-between">
                                  <span className="text-yellow-700 font-medium">Expiring</span>
                                  <span className="font-bold text-yellow-600">{employeeStats.expiring}</span>
                                </div>
                              </div>
                              <div className="bg-red-50 rounded-md px-2 py-1.5 border border-red-200">
                                <div className="flex items-center justify-between">
                                  <span className="text-red-700 font-medium">Expired</span>
                                  <span className="font-bold text-red-600">{employeeStats.expired}</span>
                                </div>
                              </div>
                              <div className="bg-red-50 rounded-md px-2 py-1.5 border border-red-200">
                                <div className="flex items-center justify-between">
                                  <span className="text-red-700 font-medium">Missing</span>
                                  <span className="font-bold text-red-600">{employeeStats.missing}</span>
                                </div>
                              </div>
                              <div className="bg-primary/10 rounded-md px-2 py-1.5 border border-primary/20">
                                <div className="flex items-center justify-between">
                                  <span className="text-primary font-medium">Booked</span>
                                  <span className="font-bold text-primary">{employeeStats.booked}</span>
                                </div>
                              </div>
                              <div className="bg-primary/10 rounded-md px-2 py-1.5 border border-primary/20">
                                <div className="flex items-center justify-between">
                                  <span className="text-primary font-medium">Quoting</span>
                                  <span className="font-bold text-primary">{employeeStats.quoting}</span>
                                </div>
                              </div>
                            </div>

                            {/* Required vs Assigned */}
                            <div className="mt-3 pt-3 border-t border-gray-200 grid grid-cols-2 gap-2 text-xs">
                              <div className="flex items-center justify-between">
                                <span className="text-gray-600">Required:</span>
                                <span className="font-bold text-gray-900">{employeeStats.required}</span>
                              </div>
                              <div className="flex items-center justify-between">
                                <span className="text-gray-600">Assigned:</span>
                                <span className="font-bold text-gray-900">{employeeStats.totalAssigned}</span>
                              </div>
                            </div>
                          </>
                        ) : (
                          <div className="rounded-lg p-3 bg-gray-50 border border-gray-200">
                            <div className="flex items-center justify-between gap-4">
                              <span className="text-xs font-medium text-gray-600">Overall Compliance</span>
                              <span className="text-lg font-bold text-gray-400">N/A</span>
                            </div>
                            <p className="text-xs text-gray-500 mt-2">No requirements assigned to this team member.</p>
                          </div>
                        )
                      )}
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>

                {/* Quick Actions Dropdown */}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button
                      className="p-1 rounded-lg flex-shrink-0 hover:bg-gray-100 transition-all duration-200"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <MoreVertical className="h-3.5 w-3.5 text-gray-400" />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-48">
                    <DropdownMenuItem onClick={(e) => {
                      e.stopPropagation();
                      navigate(`/profile/${member.user_id || member.id}`);
                    }}>
                      <Eye className="h-4 w-4 mr-2" />
                      View Profile
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={(e) => {
                      e.stopPropagation();
                      // View certificates logic would go here
                    }}>
                      <FileText className="h-4 w-4 mr-2" />
                      View Certificates
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </motion.div>
            ) : (
              <motion.div
                key="collapsed"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.2 }}
                className="relative group/avatar w-full flex justify-center py-2"
              >
                <div className="relative">
                  {member.avatar_url ? (
                    <img
                      src={member.avatar_url}
                      alt={member.name}
                      className="w-10 h-10 rounded-xl object-cover shadow-sm hover:shadow-md transition-all duration-200 hover:scale-110 cursor-pointer"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        toggleRowHighlight(member.id);
                      }}
                    />
                  ) : (
                    <div
                      className="w-10 h-10 bg-gradient-to-br from-gray-100 to-gray-200 rounded-xl flex items-center justify-center text-gray-700 text-sm font-medium shadow-sm hover:shadow-md transition-all duration-200 hover:scale-110 cursor-pointer"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        toggleRowHighlight(member.id);
                      }}
                    >
                      {member.name.split(' ').map(n => n[0]).join('').toUpperCase()}
                    </div>
                  )}
                </div>

                {/* Premium tooltip */}
                <div className="absolute left-full ml-2 top-1/2 -translate-y-1/2 px-3 py-2 bg-gray-900/95 backdrop-blur-sm text-white text-xs rounded-lg opacity-0 group-hover/avatar:opacity-100 transition-all duration-200 pointer-events-none whitespace-nowrap z-50 shadow-xl">
                  <div className="font-medium">{member.name}</div>
                  <div className="text-gray-400 text-[11px] mt-0.5">Click to highlight</div>
                  <div className="absolute top-1/2 -left-1 -translate-y-1/2 w-2 h-2 bg-gray-900/95 rotate-45"></div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </td>
  );
});

RowHeader.displayName = 'RowHeader';

/**
 * AddMemberRow - Row for adding new team members
 */
const AddMemberRow = memo(({
  nameColumnExpanded,
  visibleColumns,
  handleOpenAddMemberDialog
}) => {
  return (
    <tr className="hover:bg-blue-50/50 transition-colors duration-200">
      <td
        className="sticky left-0 z-20 bg-gradient-to-r from-blue-50 to-indigo-50 border-b border-r border-gray-200 h-[112px] cursor-pointer hover:from-blue-100 hover:to-indigo-100 transition-all duration-200"
        style={{
          width: nameColumnExpanded ? '250px' : '60px'
        }}
        onClick={handleOpenAddMemberDialog}
      >
        <div className="h-full flex items-center justify-center px-2">
          {nameColumnExpanded ? (
            <div className="flex items-center gap-3 w-full">
              <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center">
                <UserPlus className="h-5 w-5 text-blue-600" />
              </div>
              <span className="text-sm font-medium text-blue-600">Add Team Member</span>
            </div>
          ) : (
            <UserPlus className="h-5 w-5 text-blue-600" />
          )}
        </div>
      </td>
      {/* Empty cells for qualifications */}
      {visibleColumns.map((qual) => (
        <td key={qual.id} className="border-b border-gray-100 bg-blue-50/30" style={{ width: '112px', height: '112px' }}></td>
      ))}
      {/* Empty cell for add qualification column */}
      <td className="border-b border-gray-100 bg-blue-50/30" style={{ width: '112px', height: '112px' }}></td>
    </tr>
  );
});

AddMemberRow.displayName = 'AddMemberRow';

/**
 * MatrixGrid - Main virtualized grid component
 */
const MatrixGrid = memo(({
  // Data
  filteredMatrixData,
  qualStatsMap,
  employeeStatsMap,
  sidebarStats,
  filteredRequirements,

  // UI State
  nameColumnExpanded,
  hiddenColumns,
  hiddenRows,
  columnOrder,
  rowOrder,
  highlightedColumns,
  highlightedRows,

  // Handlers
  toggleColumnHighlight,
  toggleRowHighlight,
  handleRemoveQualificationFromMatrix,
  handleOpenAddQualificationDialog,
  handleOpenAddMemberDialog,
  handleCellClick,
  handleCellRightClick,
  handleHeaderRightClick,
  handleOneClickQuote,
  getCellDragProps,
  isCellDragOver,
  getStatusLabel,
  getStatusDateText,
  onColumnDragEnd,

  // Modal/Dock state
  openQualificationGapId,
  setOpenQualificationGapId,
  dockOpen,
  dockCell,
  setDockOpen,
  setDockCell,
  setSelectedCell,
  setCrossSpaceModalOpen,

  // Navigation
  projectId,

  // Loading state
  assigningCells,

  // Virtualization config
  enableVirtualization = true,
  overscan = 3
}) => {
  const navigate = useNavigate();

  // Drag-and-drop sensors for column reordering. 8px activation distance
  // ensures short clicks on headers still fire the highlight toggle.
  const columnSensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 8 }
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates
    })
  );

  // Calculate visible rows and columns based on order and hidden state
  const visibleRows = useMemo(() => {
    const visible = filteredMatrixData.teamMembers.filter(m => !hiddenRows.has(m.id));
    if (!rowOrder.length) return visible;
    const ordered = rowOrder
      .map(id => visible.find(m => m.id === id))
      .filter(Boolean);
    const orderedIds = new Set(rowOrder);
    const unordered = visible.filter(m => !orderedIds.has(m.id));
    return [...ordered, ...unordered];
  }, [filteredMatrixData.teamMembers, rowOrder, hiddenRows]);

  const visibleColumns = useMemo(() => {
    const visible = filteredMatrixData.qualifications.filter(q => !hiddenColumns.has(q.id));
    if (!columnOrder.length) return visible;
    const ordered = columnOrder
      .map(id => visible.find(q => q.id === id))
      .filter(Boolean);
    const orderedIds = new Set(columnOrder);
    const unordered = visible.filter(q => !orderedIds.has(q.id));
    return [...ordered, ...unordered];
  }, [filteredMatrixData.qualifications, columnOrder, hiddenColumns]);

  // Calculate dimensions
  const nameColumnWidth = nameColumnExpanded ? 250 : 60;
  const totalWidth = nameColumnWidth + (visibleColumns.length * CELL_WIDTH) + CELL_WIDTH; // +112 for add column

  return (
    <motion.div
      key="grid-view"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.15, ease: "easeInOut" }}
      style={{ willChange: 'opacity' }}
      onWheel={(e) => e.stopPropagation()}
    >
      <DndContext
        sensors={columnSensors}
        collisionDetection={closestCenter}
        onDragEnd={onColumnDragEnd}
      >
        <table
          className="border-separate border-spacing-0"
          style={{
            tableLayout: 'fixed',
            width: `${totalWidth}px`,
            backgroundColor: 'rgba(249, 250, 251, 0.3)'
          }}
        >
          <thead>
            <tr>
              {/* Top left corner - Overall compliance stats */}
              <CornerCell
                sidebarStats={sidebarStats}
                nameColumnExpanded={nameColumnExpanded}
              />

              {/* Sticky qualification headers - sortable */}
              <SortableContext
                items={visibleColumns.map(q => q.id)}
                strategy={horizontalListSortingStrategy}
              >
                {visibleColumns.map((qual, index) => {
                  const isHighlighted = highlightedColumns.has(qual.id);
                  const qualStats = qualStatsMap.get(qual.id);

                  return (
                    <ColumnHeader
                      key={qual.id}
                      qual={qual}
                      index={index}
                      isHighlighted={isHighlighted}
                      qualStats={qualStats}
                      toggleColumnHighlight={toggleColumnHighlight}
                      handleRemoveQualificationFromMatrix={handleRemoveQualificationFromMatrix}
                      openQualificationGapId={openQualificationGapId}
                      setOpenQualificationGapId={setOpenQualificationGapId}
                      onContextMenu={handleHeaderRightClick}
                    />
                  );
                })}
              </SortableContext>

              {/* Add Qualification column */}
              <th
                className="sticky top-[46px] z-30 bg-gradient-to-br from-blue-50 to-indigo-50 backdrop-blur-sm border-b border-gray-200 px-1 py-3 group relative h-[112px] cursor-pointer hover:from-blue-100 hover:to-indigo-100 transition-all duration-200"
                style={{ width: '112px' }}
                onClick={handleOpenAddQualificationDialog}
              >
                <div className="flex flex-col items-center justify-center gap-1">
                  <Plus className="h-4 w-4 text-blue-600" />
                  <span className="text-[11px] font-medium text-blue-600">Add Qualification</span>
                </div>
              </th>
            </tr>
          </thead>
          <tbody>
            {visibleRows.map((member, memberIndex) => {
              const isHighlighted = highlightedRows.has(member.id);
              const employeeStats = employeeStatsMap.get(member.id);

              return (
                <tr
                  key={member.id}
                  className={`hover:bg-gray-50 transition-colors ${isHighlighted ? 'bg-blue-50 border-l-2 border-r-2 border-blue-400' : ''}`}
                >
                  {/* Sticky employee name cell */}
                  <RowHeader
                    member={member}
                    isHighlighted={isHighlighted}
                    nameColumnExpanded={nameColumnExpanded}
                    employeeStats={employeeStats}
                    toggleRowHighlight={toggleRowHighlight}
                    navigate={navigate}
                  />

                  {/* Requirement cells */}
                  {visibleColumns.map((qualification) => {
                    const req = member.requirements.find(r => r.id === qualification.id);
                    if (!req) return null;

                    const isColumnHighlighted = highlightedColumns.has(qualification.id);

                    return (
                      <MatrixCell
                        key={req.id}
                        member={member}
                        qualification={qualification}
                        req={req}
                        isHighlighted={isHighlighted}
                        isColumnHighlighted={isColumnHighlighted}
                        dockOpen={dockOpen}
                        dockCell={dockCell}
                        getCellDragProps={getCellDragProps}
                        isCellDragOver={isCellDragOver}
                        handleCellClick={handleCellClick}
                        handleCellRightClick={handleCellRightClick}
                        getStatusLabel={getStatusLabel}
                        getStatusDateText={getStatusDateText}
                        setSelectedCell={setSelectedCell}
                        setCrossSpaceModalOpen={setCrossSpaceModalOpen}
                        fullRequirement={filteredRequirements.find(r => r.id === qualification.id)}
                        handleOneClickQuote={handleOneClickQuote}
                        setDockCell={setDockCell}
                        setDockOpen={setDockOpen}
                        navigate={navigate}
                        projectId={projectId}
                        isAssigning={assigningCells?.has(`${member.id}-${qualification.id}`)}
                      />
                    );
                  })}
                </tr>
              );
            })}

            {/* Add Member Row */}
            <AddMemberRow
              nameColumnExpanded={nameColumnExpanded}
              visibleColumns={visibleColumns}
              handleOpenAddMemberDialog={handleOpenAddMemberDialog}
            />
          </tbody>
        </table>
      </DndContext>
    </motion.div>
  );
});

MatrixGrid.displayName = 'MatrixGrid';

export default MatrixGrid;
